import React, { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

/**
 * VengenceUI-inspired GlowCard — cursor-tracking radial glow on border.
 *
 * Props:
 *   accent    — color variant: 'blue' (default) | 'cyan' | 'green' | 'amber' | 'red' | 'violet'
 *   padding   — CSS padding (default '1.25rem')
 *   style     — additional inline styles
 *   className — additional class names
 *   children  — card content
 *   as        — motion element type (default 'div')
 *   animate   — if true, applies Framer Motion fadeInUp entrance (default true)
 *   delay     — entrance animation delay in seconds
 *   onClick   — click handler
 */
const ACCENT_MAP = {
  blue: '',
  cyan: 'glow-card--cyan',
  green: 'glow-card--green',
  amber: 'glow-card--amber',
  red: 'glow-card--red',
  violet: 'glow-card--violet',
};

const GlowCard = React.forwardRef(({
  accent = 'blue',
  padding = '1.25rem',
  style,
  className = '',
  children,
  animate = true,
  delay = 0,
  onClick,
  ...rest
}, ref) => {
  const cardRef = useRef(null);
  const combinedRef = useCallback((node) => {
    cardRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  const handleMouseMove = useCallback((e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    el.style.setProperty('--glow-x', `${x}px`);
    el.style.setProperty('--glow-y', `${y}px`);
  }, []);

  const accentClass = ACCENT_MAP[accent] || '';
  const classes = ['glow-card', accentClass, className].filter(Boolean).join(' ');

  const motionProps = animate ? {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: 0.4,
      delay,
      ease: [0.16, 1, 0.3, 1],
    },
  } : {};

  return (
    <motion.div
      ref={combinedRef}
      className={classes}
      onMouseMove={handleMouseMove}
      onClick={onClick}
      style={{ padding, ...style }}
      {...motionProps}
      {...rest}
    >
      {children}
    </motion.div>
  );
});

GlowCard.displayName = 'GlowCard';

export default GlowCard;
